<template>
  <div>
    <a class="backln" @click.prevent="$emit('navigate', { name: 'surveys' })">{{ t('back_all') }}</a>
    <div v-if="state === 'loading'" class="dload"><v-progress-circular indeterminate color="primary" size="22" width="3" style="margin-inline-end:10px" />{{ t('d_loading') }}</div>
    <div v-else-if="state === 'missing'" class="dload">⚠️ {{ t('d_missing') }}</div>
    <template v-else-if="sv">
      <div class="dhead"><h2>{{ name }}</h2><span class="svbadge" :class="SEC_CLS[sv.secEn] || 'b3'" style="margin-top:8px">{{ isAr ? sv.secAr : sv.secEn }}</span></div>
      <div class="dkpis">
        <div class="dk"><div class="n tnum">{{ fmt(agg.parts) }}</div><div class="t">{{ t('d_parts') }}{{ ysfx }}</div></div>
        <div class="dk"><div class="n tnum">{{ fmt(agg.answers) }}</div><div class="t">{{ t('d_answers') }}{{ ysfx }}</div></div>
        <div class="dk"><div class="n tnum">{{ sv.questions || qs.length }}</div><div class="t">{{ t('d_qs') }}</div></div>
        <div class="dk"><div class="n" :style="{color: satColorPct(agg.satPct)}">{{ agg.satPct != null ? pctS(agg.satPct) : '—' }}</div><div class="t">{{ t('d_sat') }}{{ ysfx }}</div></div>
        <div class="dk"><div class="n num-ltr" style="font-size:14.5px;padding-top:6px">{{ period(sv.from, sv.to) }}</div><div class="t">{{ t('d_period') }}</div></div>
      </div>
      <div v-if="years.length > 1" class="sd-tool">
        <span class="ylab">{{ t('d_yrfilter') }}</span>
        <v-btn-toggle v-model="yearIdx" mandatory dense>
          <v-btn small text>{{ t('yr_all') }}</v-btn>
          <v-btn v-for="y in years" :key="y" small text>{{ y }}</v-btn>
        </v-btn-toggle>
      </div>
      <div v-if="agg.rated > 0" class="sd-card" style="margin-top:16px">
        <div class="head"><div><h3>{{ t('d_sat') }}</h3><p class="hint">{{ t('d_ratedhint', { n: fmt(agg.rated) }) }}{{ ysfx }}</p></div><sd-star-pill v-if="agg.score != null" :score="agg.score" /></div>
        <sd-sat-bar :buckets="agg.B" legend />
      </div>
      <sd-section-title :title="t('d_qres')" :note="t('d_qshow', { m: ql.length, n: qsAll.length })" style="margin-top:28px">
        <v-text-field v-model="qq" class="sd-search" dense outlined hide-details prepend-inner-icon="mdi-magnify" :placeholder="t('d_qsearch')" />
        <v-btn color="primary" depressed small class="sd-btn" @click="exportSurveyCsv(sv)"><v-icon small left>mdi-download</v-icon>{{ t('exp_csv_q') }}</v-btn>
      </sd-section-title>
      <div v-for="(q, qi) in ql" :key="q.id" class="qcard">
        <div class="qh">
<span class="qnum">{{ qi + 1 }}</span>
          <span class="qt">{{ (isAr ? q.tAr : q.tEn) || q.tAr || q.tEn || ('#' + q.id) }} <span v-if="q.del" class="qtype" style="color:var(--sd-crit)">{{ t('d_del') }}</span></span>
          <span v-if="typeLabel(q)" class="qtype">{{ typeLabel(q) }}</span>
</div>
        <div class="qtot tnum">{{ fmt(qTot(q)) }} {{ t('d_ans_u') }}{{ ysfx }}</div>
        <div v-for="(o, oi) in (q.os || [])" :key="oi" class="orow">
          <span class="ot" :title="optName(o)">{{ optName(o) }}</span>
          <span class="om"><i :style="{width: (ready ? optPct(q, o) : 0) + '%', background: o.b ? BUCKET_C[o.b] : 'var(--sd-green-500)'}" /></span>
          <span class="ov"><b class="num-ltr">{{ Math.round(optPct(q, o) * 10) / 10 }}%</b> · {{ fmt(ySum(o.y, year)) }}</span>
        </div>
        <div v-if="ySum(q.tx, year)" class="txrow">✍ <b class="tnum">{{ fmt(ySum(q.tx, year)) }}</b> {{ t('d_txt') }}</div>
      </div>
    </template>
  </div>
</template>
<script>
import { sdMixin } from '../store.js'
import { fmt, pctS, period, satColorPct, SEC_CLS, BUCKET_C, ySum, detailName } from '../lib/format.js'
import { MESSAGES } from '../i18n/messages.js'
import { exportSurveyCsv } from '../lib/exportCsv.js'
import SdSectionTitle from '../components/SectionTitle.vue'
import SdStarPill from '../components/StarPill.vue'
import SdSatBar from '../components/charts/SatBar.vue'
export default {
  name: 'SdSurveyDetailView',
  components: { SdSectionTitle, SdStarPill, SdSatBar },
  mixins: [sdMixin],
  props: { id: { type: [Number, String], required: true } },
  data: () => ({ state: 'loading', yearIdx: 0, qq: '', ready: false, SEC_CLS, BUCKET_C }),
  computed: {
    sv() { return this.$sd.state.details[+this.id] },
    name() { return detailName(this.sv, this.lang) },
    years() { return [...new Set([...(this.sv.yrs || []).map((r) => r[0]), ...(this.sv.satYr || []).map((r) => r[0])])].filter((y) => y >= 2019 && y <= 2031).sort() },
    year() { return this.yearIdx > 0 ? this.years[this.yearIdx - 1] : null },
    ysfx() { return this.year ? ' · ' + this.year : '' },
    qs() { return this.sv.qs || [] },
    agg() {
      let answers = 0; const B = { VS: 0, S: 0, NU: 0, D: 0, VD: 0 }
      this.qs.forEach((q) => { (q.os || []).forEach((o) => { const c = ySum(o.y, this.year); answers += c; if (o.b && B[o.b] != null) B[o.b] += c }); answers += ySum(q.tx, this.year) })
      const rated = B.VS + B.S + B.NU + B.D + B.VD
      return { answers, B, rated, satPct: rated >= 50 ? Math.round((B.VS + B.S) / rated * 1000) / 10 : null, score: rated ? (5 * B.VS + 4 * B.S + 3 * B.NU + 2 * B.D + B.VD) / rated : null,
        parts: this.year ? (this.sv.yrs || []).filter((r) => r[0] === this.year).reduce((a, r) => a + r[1], 0) : (this.sv.parts || 0) }
    },
    qsAll() { return this.qs.filter((q) => q.tot > 0 || !q.del) },
    ql() { const q = this.qq.trim().toLowerCase(); return q ? this.qsAll.filter((x) => ((x.tAr || '') + ' ' + (x.tEn || '')).toLowerCase().includes(q)) : this.qsAll },
  },
  watch: { id: { immediate: true, handler() { this.load() } } },
  methods: {
    fmt, pctS, period, satColorPct, ySum, exportSurveyCsv,
    async load() {
      this.state = 'loading'; this.yearIdx = 0; this.qq = ''; this.ready = false
      try { await this.$sd.survey(this.id); this.state = 'ok'; this.$nextTick(() => requestAnimationFrame(() => { this.ready = true })) }
      catch (e) { this.state = 'missing' }
    },
    typeLabel(q) { const k = 'qt' + q.ty; return MESSAGES[k] ? this.t(k) : '' },
    qTot(q) { return (q.os || []).reduce((a, o) => a + ySum(o.y, this.year), 0) + ySum(q.tx, this.year) },
    optPct(q, o) { const tot = this.qTot(q); return tot ? ySum(o.y, this.year) / tot * 100 : 0 },
    optName(o) { return (this.isAr ? o.a : o.e) || o.a || o.e || '—' },
  },
}
</script>
